/* tslint:disable:object-literal-shorthand */
import {createAction, props, Store} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, withLatestFrom} from 'rxjs/operators';
import {
  ActionFailed,
  actionFailed, actionsLoaded, InflationCorrectionDisabled,
  inflationCorrectionDisabled,
  InflationCorrectionEnabled,
  inflationCorrectionEnabled, InflationRateChanged, inflationRateChanged, initialScenarioAdded, InitialScenarioAdded,
  LogViewChanged,
  logViewChanged,
  MortgageAdded,
  mortgageAdded,
  MortgageRemoved,
  mortgageRemoved,
  MortgageUpdated,
  mortgageUpdated,
  paymentAdded,
  PaymentAdded,
  PaymentRemoved,
  paymentRemoved,
  PaymentUpdated,
  paymentUpdated,
  ScenarioCopied,
  scenarioCopied,
  ScenarioRemoved,
  scenarioRemoved
} from './reducer.events';
import {MortgageId, PaymentId, ScenarioId} from '../domain/scenario.model';
import {
  AppState,
  ContractType,
  FinanceState,
  financeStateId,
  MortgageContract,
  PaymentOverview,
  PaymentPlan, Scenario
} from './AppState';
import {lastScenarioId, newMortgageId, newScenarioId} from './utils';
import {TypedAction} from '@ngrx/store/src/models';
import {AnalyticsService} from '../common/analytics.service';

export interface ChangeLogView {
  showLog: boolean;
}

export interface ToggleInflationCorrection extends HasScenarioId {
  scenarioId: ScenarioId;
  toggleInflationCorrection: boolean;
}

export interface ChangeInflationRate extends HasScenarioId {
  scenarioId: ScenarioId;
  rate: number;
}

export interface AddInitialScenario {
  scenario: Scenario;
}

export interface AddMortgage extends HasScenarioId {
  scenarioId: ScenarioId;
  contract: MortgageContract;
}

export interface AddPayment extends HasScenarioId, HasMortgageId {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  plan: PaymentPlan;
}

export interface UpdatePayment extends HasScenarioId, HasMortgageId, HasPaymentId {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  paymentId: PaymentId;
  plan: PaymentPlan;
}

export interface RemovePayment extends HasScenarioId, HasMortgageId, HasPaymentId {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
  paymentId: PaymentId;
}

export interface UpdateMortgage extends HasScenarioId, HasMortgageId {
  scenarioId: ScenarioId;
  contract: MortgageContract;
  mortgageId: MortgageId;
}

export interface RemoveMortgage extends HasScenarioId, HasMortgageId {
  scenarioId: ScenarioId;
  mortgageId: MortgageId;
}

export interface RemoveScenario extends HasScenarioId, HasMortgageId {
  scenarioId: ScenarioId;
}

export interface HasScenarioId {
  scenarioId: ScenarioId;
}

export interface HasMortgageId {
  mortgageId: MortgageId;
}

export interface HasPaymentId {
  paymentId: PaymentId;
}

export const loadActions = createAction('[Action] Load actions');
export const changeLogView = createAction('[Action] Change log view', props<ChangeLogView>());
export const toggleInflationCorrection = createAction('[Action] Toggle inflation correction', props<ToggleInflationCorrection>());
export const changeInflationRate = createAction('[Action] Changed inflation rate', props<ChangeInflationRate>());
export const addMortgage = createAction('[Action] Add mortgage', props<AddMortgage>());
export const addPayment = createAction('[Action] Add payment', props<AddPayment>());
export const updatePayment = createAction('[Action] Update payment', props<UpdatePayment>());
export const removePayment = createAction('[Action] Remove payment', props<RemovePayment>());
export const updateMortgage = createAction('[Action] Update mortgage', props<UpdateMortgage>());
export const removeMortgage = createAction('[Action] Remove mortgage', props<RemoveMortgage>());
export const addInitialScenario = createAction('[Action] Add Initial scenario');
export const addScenario = createAction('[Action] Copy scenario');
export const removeScenario = createAction('[Action] Remove scenario', props<RemoveScenario>());


@Injectable()
export class EffectsActions {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private analytics: AnalyticsService) {
  }

  handleLoadActions = createEffect(() => this.actions$.pipe(
    ofType(loadActions),
    withLatestFrom(this.store$),
    map(([action, state]) => {
        return actionsLoaded();
      })
    ));

  handleChangeLogView = createEffect(() => this.actions$
    .pipe(
      ofType(changeLogView),
      withLatestFrom(this.store$.pipe(map(state => state.config))),
      map(([payload, state]) => {
        this.analytics.action('changeLogView', {
          showLog: payload.showLog
        });

        return logViewChanged({
          showLog: payload.showLog
        } as LogViewChanged);
      })
    ));


  handleToggleInflationCorrection = createEffect(() => this.actions$
    .pipe(
      ofType(toggleInflationCorrection),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('toggleInflationCorrection', {
          toggleInflationCorrection: payload.toggleInflationCorrection
        });


        if (state.scenarios[payload.scenarioId]
        ) {
          if (payload.toggleInflationCorrection) {
            return inflationCorrectionEnabled({
              scenarioId: payload.scenarioId
            } as InflationCorrectionEnabled);
          } else {
            return inflationCorrectionDisabled({
              scenarioId: payload.scenarioId
            } as InflationCorrectionDisabled);
          }
        }else {
          const failed = actionFailed({
            action: toggleInflationCorrection.type,
            payload: payload,
            reason: 'scenario to update does not exist'
          } as ActionFailed);
          this.analytics.action('actionFailed', {
            action: toggleInflationCorrection.type,
            msg: 'scenario to update does not exist'
          });
          return failed;
        }
      })
    ));

  handleChangeInflationRate = createEffect(() => this.actions$
    .pipe(
      ofType(changeInflationRate),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('changeInflationRate', {
          inflationRate: payload.rate
        });

        if (state.scenarios[payload.scenarioId]
        ) {
          return inflationRateChanged({
            scenarioId: payload.scenarioId,
            rate:payload.rate
          } as InflationRateChanged);
        } else {
          const failed = actionFailed({
            action: changeInflationRate.type,
            payload: payload,
            reason: 'scenario to update does not exist'
          } as ActionFailed);
          this.analytics.action('actionFailed', {
            action: changeInflationRate.type,
            msg: 'scenario to update does not exist'
          });
          return failed;
        }
      })
    ));

  handleAddMortgage = createEffect(() => this.actions$
    .pipe(
      ofType(addMortgage),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('addMortgage', {
          contractType: payload.contract.contractType
        });

        return mortgageAdded({
          contract: payload.contract,
          scenarioId: payload.scenarioId,
          mortgageId: newMortgageId(state.scenarios[payload.scenarioId])
        } as MortgageAdded);
      })
    ));

  handleAddPayment = createEffect(() => this.actions$
    .pipe(
      ofType(addPayment),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('addPayment', {
          plan: payload.plan.planType
        });

        return this.givenMortgageExists(payload, state, (mortgage) => {

          let paymentId: PaymentId;
          if (mortgage.lastPaymentId) {
            paymentId = String(+mortgage.lastPaymentId + 1);
          } else {
            paymentId = '1';
          }

          return paymentAdded({
            scenarioId: payload.scenarioId,
            mortgageId: payload.mortgageId,
            paymentId: paymentId,
            plan: payload.plan
          } as PaymentAdded);
        });
      })
    ));


  handleUpdatePayment = createEffect(() => this.actions$
    .pipe(
      ofType(updatePayment),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('updatePayment', {
          plan: payload.plan.planType
        });

        if (state
          .scenarios[payload.scenarioId]
          .mortgages[payload.mortgageId]
          .payments[payload.paymentId]
        ) {
          return paymentUpdated({
            scenarioId: payload.scenarioId,
            mortgageId: payload.mortgageId,
            paymentId: payload.paymentId,
            plan: payload.plan
          } as PaymentUpdated);
        } else {
          const failed = actionFailed({
            action: updatePayment.type,
            payload: payload,
            reason: 'payment to update does not exist'
          } as ActionFailed);
          this.analytics.action('actionFailed', {
            action: updatePayment.type,
            msg: 'payment to update does not exist'
          });
          return failed;
        }
      })
    ));

  handleRemovePayment = createEffect(() => this.actions$
    .pipe(
      ofType(removePayment),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('removePayment');
        return this.givenPaymentExists(payload, state, () => {
          return paymentRemoved({
            scenarioId: payload.scenarioId,
            mortgageId: payload.mortgageId,
            paymentId: payload.paymentId
          } as PaymentRemoved);
        });
      })
    ));

  handleRemoveMortgage = createEffect(() => this.actions$
    .pipe(
      ofType(removeMortgage),
      withLatestFrom(this.store$.pipe(map(state => state[financeStateId]))),
      map(([payload, state]) => {
        this.analytics.action('removeMortgage');
        return this.givenMortgageExists(payload, state, () => {
          return mortgageRemoved({
            scenarioId: payload.scenarioId,
            mortgageId: payload.mortgageId
          } as MortgageRemoved);
        });
      })
    ));

  handleUpdateMortgage = createEffect(() => this.actions$
    .pipe(
      ofType(updateMortgage),
      map((payload) => {
        this.analytics.action('updateMortgage', {
          contractType: payload.contract.contractType
        });

        let durationMonths: number | null = payload.contract.durationMonths;

        if (payload.contract.contractType === ContractType.LINEAR) {
          if (payload.contract.durationMonths != null) {
            durationMonths = payload.contract.durationMonths;
          } else {
            durationMonths = 0; // TODO: should result in failure
          }
        } else if (payload.contract.contractType === ContractType.INTEREST_ONLY) {
          if (payload.contract.durationMonths != null) {
            durationMonths = payload.contract.durationMonths;
          } else {
            durationMonths = 0; // TODO: should result in failure
          }
        }

        return mortgageUpdated({
          contract: {
            ...payload.contract,
            durationMonths: durationMonths
          },
          scenarioId: payload.scenarioId,
          mortgageId: payload.mortgageId
        } as MortgageUpdated);
      })
    ));

  handleAddInitialScenario = createEffect(() => this.actions$.pipe(
    ofType(addInitialScenario),
    withLatestFrom(this.store$),
    map(([payload, state]) => {
      this.analytics.action('addInitialScenario');

      if (lastScenarioId(state[financeStateId]) == null) {
        return initialScenarioAdded({
          scenario: {
            id: '1',
            inflationRate: 1.2,
            useInflationCorrection: false,
            mortgageCount: 0,
            mortgages: {},
          } as Scenario
        } as InitialScenarioAdded);
      } else {
        const failed = actionFailed({
          action: addInitialScenario.type,
          payload: payload,
          reason: 'there already exists a scenario'
        } as ActionFailed);
        this.analytics.action('actionFailed', {
          action: addInitialScenario.type,
          msg: 'there already exists a scenario'
        });
        return failed;
      }
    })
  ));


  handleAddScenario = createEffect(() => this.actions$.pipe(
    ofType(addScenario),
    withLatestFrom(this.store$),
    map(([action, state]) => {
      this.analytics.action('addScenario');

      if (lastScenarioId(state[financeStateId]) != null) {
        return scenarioCopied({
          scenarioId: newScenarioId(state[financeStateId])
        } as ScenarioCopied);
      } else {
        return initialScenarioAdded({
          scenario: {
            id: '1',
            inflationRate: 1.2,
            useInflationCorrection: false,
            mortgageCount: 0,
            mortgages: {},
          } as Scenario
        } as InitialScenarioAdded);
      }
    })
  ));

  handleRemoveScenario = createEffect(() => this.actions$.pipe(
    ofType(removeScenario),
    withLatestFrom(this.store$),
    map(([action, state]) => {
      this.analytics.action('removeScenario');
      return scenarioRemoved({
          scenarioId: action.scenarioId
        } as ScenarioRemoved
      );
    })
  ));

  private givenScenarioExists(
    payload: HasScenarioId & TypedAction<any>,
    state: FinanceState,
    block: (Scenario) => TypedAction<any>
  ) {
    const scenario = state.scenarios[payload.scenarioId];
    if (scenario != null) {
      return block(scenario);
    } else {
      this.analytics.action('actionFailed', {
        action: payload.type,
        msg: 'scenario doesnt exist'
      });
      return actionFailed({
        action: payload.type,
        payload: payload,
        reason: 'scenario doesnt exist'
      } as ActionFailed);
    }
  }

  private givenMortgageExists(
    payload: HasMortgageId & HasScenarioId & TypedAction<any>,
    state: FinanceState,
    block: (MortgageOverview) => TypedAction<any>
  ) {
    return this.givenScenarioExists(payload, state, (scenario) => {
      const mortgage = scenario.mortgages[payload.mortgageId];
      if (mortgage != null) {
        return block(mortgage);
      } else {
        this.analytics.action('actionFailed', {
          action: payload.type,
          msg: 'mortgage doesnt exist'
        });
        return actionFailed({
          action: payload.type,
          payload: payload,
          reason: 'mortgage doesnt exist'
        } as ActionFailed);
      }
    });
  }

  private givenPaymentExists(
    payload: HasMortgageId & HasScenarioId & HasPaymentId & TypedAction<any>,
    state: FinanceState,
    block: (payment: PaymentOverview) => TypedAction<any>
  ) {
    return this.givenMortgageExists(payload, state, (mortgage) => {
      const payment = mortgage.payments[payload.paymentId];
      if (payment != null) {
        return block(payment);
      } else {
        this.analytics.action('actionFailed', {
          action: payload.type,
          msg: 'payment doesnt exist'
        });
        return actionFailed({
          action: payload.type,
          payload: payload,
          reason: 'payment doesnt exist'
        } as ActionFailed);
      }
    });
  }
}
